import AbstractModelType from '../AbstractModelType';
import BookMarker from '../BookMarker';
import BookPage from '../BookPage';
import BookDocument from '../BookDocument';

/**
 * The book model does not include associations with user content.
 * We could say book.markers[0].notes = [myNote, myOtherNote] but then the book object would no longer be a pure representation of book data,
 * but an amalgam of book data and user content. The book object should only change if the book is changed on the backend.
 */
class Book extends AbstractModelType {
  #processingReportPk;

  #documents = [];
  #pages = [];
  #markers = [];
  #topLevelMarkers = [];

  constructor(properties) {
    super(...arguments);

    this.#processingReportPk = +properties.latestProcessingReportPk;

    // All of these properties that are not from the skeleton JSON could in theory be obtained without processing.
    // A RedShelf source-of-truth endpoint could be made available for book metadata like title, author.
    // The same way we have DRM set up, but not proxied. Search could be tricky.
    this.title = properties.title?.trim();
    this.author = properties.author?.trim();
    this.authorFirst = properties.authorFirst?.trim();
    this.authorLast = properties.authorLast?.trim();
    this.editors = properties.editors?.trim();
    this.translators = properties.translators?.trim();
    this.publisher = properties.publisher?.trim();
    this.volume = properties.volume?.trim();
    this.year = properties.year;
    this.coverImage = properties.coverImage;

    this.notesDisabled = Boolean(properties.notesDisabled);
    this.collaborationDisabled = Boolean(properties.collaborationDisabled);
    this.flashcardsDisabled = Boolean(properties.flashcardsDisabled);

    this.isPublisherEncryptionEnabled = properties.encryptContent;
    this.isIndexedForSearch = Boolean(properties.indexedForSearchAt); // elasticsearch backend search
    this.hasLunrSearchIndex = Boolean(properties.lunrSearchIndexUpdatedAt); // lunr frontend search
    this.useElasticsearchInsteadOfLunr = Boolean(
      properties.useElasticsearchInsteadOfLunr,
    ); // per-book setting to use elasticsearch instead of lunr

    // The following data is computed during processing
    this.characterCount = properties.characterCount;

    for (let document of properties.documents ?? []) {
      this.addDocument(document);
    }

    for (let page of properties.pages ?? []) {
      this.addPage(page);
    }

    for (let marker of properties.markers ?? []) {
      this.addMarker(marker);
    }

    this.addTopLevelMarkers(this.#markers);

    // Go backwards through the pages and spine entries and determine percentage location from char counts.
    // Also create next/previous pointers.
    let pageCharsCounted = 0;
    for (let page of this.pages) {
      if (!page) {
        continue;
      }

      // Pages must use a backwards iteration because the character count leading up to the first page is unknown.
      if (page) {
        let bookCharacterCount = this.characterCount || this.pages.length;
        let pageCharacterCount = this.characterCount ? page.characterCount : 1;

        page.percentageEnd =
          1 -
          (bookCharacterCount - pageCharsCounted - pageCharacterCount) /
            bookCharacterCount;

        page.percentageStart =
          1 - (bookCharacterCount - pageCharsCounted) / bookCharacterCount;

        pageCharsCounted += pageCharacterCount;
      }
    }

    let documentCharsCounted = 0;
    for (let document of this.documents) {
      if (!document) {
        continue;
      }
      let bookCharacterCount = this.characterCount || this.documents.length;
      let documentCharacterCount = this.characterCount
        ? document.characterCount
        : 1;

      document.percentageStart = documentCharsCounted / bookCharacterCount;

      document.percentageEnd =
        (documentCharsCounted + documentCharacterCount) / bookCharacterCount;

      documentCharsCounted += documentCharacterCount;
    }

    this.#documents.forEach((document) => document.setDerivedProperties());
    this.#pages.forEach((page) => page.setDerivedProperties());
    this.#markers.forEach((marker) => marker.setDerivedProperties());

    Object.freeze(this); // no touchy, brrr
    this.#documents.forEach(Object.freeze);
    this.#pages.forEach(Object.freeze);
    this.#markers.forEach(Object.freeze);
  }

  static createFromData({ epubByPk: { ...properties } }) {
    const skeleton = JSON.parse(properties.skeleton);
    properties.characterCount = skeleton.book.characterCount;

    properties.documents = skeleton.documents.map((d) => new BookDocument(d));
    properties.pages = skeleton.pages.map((d) => new BookPage(d));
    properties.markers = skeleton.markers.map((d) => new BookMarker(d));

    return new this(properties);
  }

  addDocument(document) {
    document.book = this;
    this.#documents[document.index] = document;
  }

  addPage(page) {
    page.book = this;
    this.#pages[page.index] = page;
  }

  addMarker(marker) {
    marker.book = this;
    this.#markers[marker.index] = marker;
  }

  addTopLevelMarkers(markers) {
    const topLevelMarkers = [];

    for (let marker of markers) {
      if (marker?.isTopLevelMarker) {
        topLevelMarkers.push(marker);
      }
    }

    this.#topLevelMarkers = topLevelMarkers;
  }

  get processingReportPk() {
    return this.#processingReportPk;
  }

  get documents() {
    return this.#documents;
  }

  get pages() {
    return this.#pages;
  }

  get markers() {
    return this.#markers;
  }

  get topLevelMarkers() {
    return this.#topLevelMarkers;
  }

  documentsByIndices(indices) {
    return indices
      .map((i) => this.#documents[i])
      .filter((m) => m instanceof BookDocument);
  }

  pagesByIndices(indices) {
    return indices
      .map((i) => this.#pages[i])
      .filter((m) => m instanceof BookPage);
  }

  markersByIndices(indices) {
    return indices
      .map((i) => this.#markers[i])
      .filter((m) => m instanceof BookMarker);
  }

  findFirstPageWithLabel(label) {
    for (let page of this.pages) {
      if (label === page?.label.trim().toLowerCase()) {
        return page;
      }
    }

    return null;
  }

  getPercentageOfBookForCharacterCount(characterCount) {
    // No characters in the book
    if (!this.characterCount) {
      return 0;
    }
    return Math.ceil((characterCount / this.characterCount) * 1000) / 1000;
  }
}

export default Book;
